import React, { useMemo, useState } from 'react';
import { formatMoney } from 'accounting';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { renderModal } from 'utils/modal_util';

import Modal from 'components/shared/Modal';
import TooltipModalContent from 'components/shared/TooltipModalContent';
import OptionButton from 'components/features/ProductSidebar/OptionButtonContainer';
import Filter from 'components/shared/Filter';

import optionGroups from 'domains/option_groups';
import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';

import { selectOptionPrices } from 'selectors/prices';
import { selectCustomPriceLoading } from 'selectors/loading_states';

import type { OptionGroupAPIData } from 'domains/option_groups';
import type { TooltipModalContentProps } from 'components/shared/TooltipModalContent/TooltipModalContent';
import type { Pair } from 'types/common_types';

type OptionGroupProps = {
  optionGroup: OptionGroupAPIData;
};

function OptionGroup({ optionGroup }: OptionGroupProps) {
  const { name, id: optionGroupId } = optionGroup;
  const [tooltipDialog, setTooltipDialog] = useState<React.ReactNode>(null);

  const dispatch = useAppDispatch();
  const allOptionGroupFilters = useAppSelector(activeEditor.selectors.optionGroupFilters);
  const disabledOptions = useAppSelector(activeEditor.selectors.disabledOptions);
  const appliedOptionGroupFilters = useAppSelector(optionGroups.selectors.appliedFilters);
  const customPriceLoading = useAppSelector(selectCustomPriceLoading);
  const optionPrices = useAppSelector(selectOptionPrices);

  const optionGroupPrice = useMemo(() => {
    if (!optionPrices[optionGroupId] || optionPrices[optionGroupId] <= 0) {
      return null;
    }

    return formatMoney(optionPrices[optionGroupId]);
  }, [optionPrices, optionGroupId]);

  const currentFilter = useMemo(() => {
    return appliedOptionGroupFilters[optionGroup.id];
  }, [appliedOptionGroupFilters, optionGroup.id]);

  const handleTooltipSelect = (id: number, groupId: number) => {
    const optionPair: Pair<number, number> = [groupId, id];
    dispatch(canvases.actions.changeActiveCanvasConfig.options({ options: [optionPair] }));
    setTooltipDialog(null);
  };

  const options = useMemo(() => {
    return optionGroup.options.filter((option) => {
      return (
        !option.hidden &&
        ((!optionGroup.filter_default && !option.filter_group) ||
          !currentFilter ||
          currentFilter === 'All' ||
          option.filter_group.split(',').find((fg) => fg === currentFilter))
      );
    });
  }, [currentFilter, optionGroup]);

  const sectionClass = useMemo(() => {
    if (optionGroup.hidden) {
      return 'hidden';
    }
    if (optionGroup.options.length === 0) {
      return 'disclaimer';
    }

    return '';
  }, [optionGroup]);

  const tooltipModalData: TooltipModalContentProps[] = useMemo(() => {
    return options.map((option) => {
      const {
        id,
        tooltip_title: tooltipTitle,
        tooltip_description: tooltipDescription,
        tooltip_img: tooltipImg,
        tooltip_url: tooltipUrl,
      } = option;

      return {
        id,
        parentId: optionGroup.id,
        readOnly: !!disabledOptions[id] || option.read_only || !!option.unavailable,
        tooltipTitle,
        tooltipDescription,
        tooltipImg,
        tooltipUrl,
        handleTooltipSelect,
      };
    });
  }, [options]);

  function handleOpenTooltipModal(optionId: number, clickedOptionButtonId: string) {
    // we pass option uid as the id to tooltip data
    const pageStartIndex = tooltipModalData.findIndex((option) => option.id === optionId);
    setTooltipDialog(
      <Modal
        labelledBy="option-tooltip-modal"
        lastFocusedElementId={clickedOptionButtonId}
        onClose={() => setTooltipDialog(null)}
        title={optionGroup.name}
        pageStartIndex={pageStartIndex}
      >
        {tooltipModalData.map((option) => (
          <TooltipModalContent key={option.id} {...option} />
        ))}
      </Modal>
    );
  }

  function handleSelectFilter(filter: string) {
    dispatch(optionGroups.actions.setAppliedOptionGroupFilters({ [optionGroupId]: filter }));
  }

  // If there are no options to show, but options do exist, we should not render the section
  if (optionGroup.options.length > 0 && options.length === 0) {
    return null;
  }

  return (
    <React.Fragment>
      <div className={`sidebar-section option-section ${sectionClass}`}>
        <header className="sidebar-section__header flex">
          {optionGroup.options.length === 0 && <FontAwesomeIcon icon="exclamation-triangle" />}
          <h2 dangerouslySetInnerHTML={{ __html: name }} />
          {optionGroupPrice && (
            <div className={`price ${customPriceLoading ? 'is-dirty' : ''}`}>
              {optionGroupPrice}
            </div>
          )}
        </header>
        <Filter
          filterUI={optionGroup.filter_default ? optionGroup.filter_ui : 'none'}
          selectedFilter={appliedOptionGroupFilters[optionGroup.id] || 'All'}
          filters={allOptionGroupFilters[optionGroup.id] || []}
          filterType="options"
          handleSelectFilter={handleSelectFilter}
        />
        <div className="option-group">
          {options.map((option) => {
            return (
              <div className="option" key={option.id}>
                <OptionButton
                  key={option.id}
                  option={option}
                  optionGroup={optionGroup}
                  handleOpenTooltipModal={handleOpenTooltipModal}
                />
              </div>
            );
          })}
        </div>
      </div>

      {tooltipDialog && renderModal(tooltipDialog)}
    </React.Fragment>
  );
}

export default OptionGroup;
